import { Component, ElementRef, OnInit, Renderer2, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { Observable, of, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LoaderService } from "../../loader/loader.service";
import { PermissionsService } from "../../services/permissions.service";
import { ChargesDistributionService } from "./services/charges-distribution.service";
import { Charge } from "./models/charge";
import { ChargeResult } from "./models/charge-result";
import { ChargeFilter } from "./models/charge-filter";
import { ChargeConfig } from "./models/charge-config";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { ChargeLinked } from "./models/charge-linked";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { UsersService } from "../../users/users.service";
import { User } from "../../users/models/user";
import { ChargeSync } from "./models/charge-sync";

@Component({
    selector: "app-charges-distribution",
    templateUrl: "./charges-distribution.component.html",
    styleUrls: ["./charges-distribution.component.scss"],
})
export class ChargesDistributionComponent implements OnInit {
    env;
    chargesResult: ChargeResult;
    charges$: Observable<Charge[]>;
    chargesLinked: ChargeLinked[] = [];

    chargeFilter: ChargeFilter;
    chargeConfig: ChargeConfig;

    checkAll: boolean;
    chargesItemsSelected: number = 0;

    modalActive: NgbModalRef;
    collectors: User[];
    collectorSelected: string;
    schedulingDate: Date;

    page = 1;
    pageSize = 10;
    collectionSize = 0;

    cityDropdownList = [];
    cityDropdownSettings: IDropdownSettings = {};

    neighborhoodDropdownList = [];
    neighborhoodDropdownSettings: IDropdownSettings = {};

    ufDropdownList = [];
    ufDropdownSettings: IDropdownSettings = {};

    collectorDropdownList = [];
    collectorDropdownSettings: IDropdownSettings = {};

    chargeAreas: { descricao_area: string; codigo_area: string }[] = [];
    selectedChargeArea: string;

    private unsubscribe$ = new Subject();
    private subjectSyncCharges$ = new Subject<Promise<ChargeSync>>();
    private syncChargesFlag: boolean = true;

    timeoutIDWarn;
    timeoutIDSuccess;

    @ViewChild("alertWarn", { read: ElementRef, static: false })
    alertWarn: ElementRef;

    @ViewChild("alertSuccess", { read: ElementRef, static: false })
    alertSuccess: ElementRef;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private loaderService: LoaderService,
        public permissionsService: PermissionsService,
        private chargesDistService: ChargesDistributionService,
        private modalService: NgbModal,
        private usersService: UsersService,
        private renderer: Renderer2
    ) {
        this.env = environment;
    }

    ngOnInit() {
        this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(async params => {
            if (this.permissionsService.isPermitted("cobrancaDist")) {
                this.setSyncChargesObservable();
                this.setInitialFilters();
                await this.setDropdowns(this.env.selected.cnpj);
                await this.refreshData();
                this.fetchChargeAreas();
            } else {
                this.router.navigateByUrl(`/dash/${params.cnpj}`);
            }
        });
    }

    addWarnAlert(msg: string) {
        clearTimeout(this.timeoutIDWarn);

        this.renderer.setProperty(this.alertWarn.nativeElement, "innerHTML", msg);
        this.renderer.setProperty(this.alertWarn.nativeElement, "hidden", false);

        this.timeoutIDWarn = setTimeout(_ => {
            this.renderer.setProperty(this.alertWarn.nativeElement, "hidden", true);
        }, 8000);
    }

    addWarnSuccess(msg: string) {
        clearTimeout(this.timeoutIDSuccess);

        this.renderer.setProperty(this.alertSuccess.nativeElement, "innerHTML", msg);
        this.renderer.setProperty(this.alertSuccess.nativeElement, "hidden", false);

        this.timeoutIDSuccess = setTimeout(_ => {
            this.renderer.setProperty(this.alertSuccess.nativeElement, "hidden", true);
        }, 8000);
    }

    async getCharges(cnpj: string) {
        const loader = await this.loaderService.notifica({
            title: "Buscando cobranças...",
            date: moment().toISOString(),
            type: "loading",
        });

        this.chargesResult = await this.chargesDistService
            .getCharges(cnpj, this.chargeFilter, this.page, this.pageSize)
            .toPromise();
        this.collectionSize = this.chargesResult.totalDocs;
        this.charges$ = of(
            this.chargesResult.result.map(charge => {
                this.chargesLinked.find(
                    linked => linked.cgc_cliente === charge.cgc_cliente && linked.contrato === charge.contrato
                )
                    ? (charge.selected = true)
                    : (charge.selected = false);

                return charge;
            })
        );

        loader.dismiss();
    }

    setSyncChargesObservable() {
        this.subjectSyncCharges$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(_ => this.syncChargesFlag)
            )
            .subscribe(async chargeSyncPromise => {
                this.syncChargesFlag = false;

                const loader = await this.loaderService.notifica({
                    title: "Atualizando cobrança...",
                    date: moment().toISOString(),
                    type: "loading",
                });

                const chargeSync = await chargeSyncPromise;

                loader.dismiss();

                if (chargeSync && chargeSync.ok) {
                    this.addWarnSuccess("Cobrança atualizada com sucesso.");
                    await this.refreshData();
                }

                if (chargeSync && chargeSync.error) {
                    this.addWarnAlert(`Alerta atualização de cobrança: ${chargeSync.msg.trim()}`);
                }

                this.syncChargesFlag = true;
            });
    }

    setInitialFilters() {
        this.chargeFilter = {
            dueDate: { fromDate: null, toDate: null },
            overdueInstallments: { start: null, end: null },
            scheduling: { fromDate: null, toDate: null },
            schedulingCollector: { fromDate: null, toDate: null },
            collector: [],
            uf: [],
            city: [],
            contracts: [],
            clients: [],
            neighborhoods: [],
            status: "ambos",
            homeCharge: false,
        };

        this.chargeConfig = { ufs: [], municipios: [], bairros: [] };

        this.page = 1;
        this.pageSize = 10;
        this.collectionSize = 0;
    }

    async setDropdowns(cnpj: string) {
        this.chargeConfig = await this.chargesDistService.getFilterChargeConfigs(cnpj).toPromise();
        this.collectors = await this.usersService.getUsersCollectors(this.env.selected.cnpj).toPromise();

        this.setCityDropdown();
        this.setNeighborhoodDropdown();
        this.setUfDropdown();
        this.setCollectorDropdown();
    }

    setCityDropdown() {
        this.cityDropdownList = this.chargeConfig.municipios.filter(
            municipio => municipio.municipio != null && municipio.municipio != ""
        );
        this.cityDropdownSettings = {
            singleSelection: true,
            idField: "municipio",
            textField: "municipio",
            selectAllText: "Selecionar Todos",
            unSelectAllText: "Limpar Seleção",
            itemsShowLimit: 2,
            allowSearchFilter: true,
            searchPlaceholderText: "Pesquisar",
            noDataAvailablePlaceholderText: "Sem dados disponíveis",
        };
    }

    setNeighborhoodDropdown() {
        this.neighborhoodDropdownList = this.chargeConfig.bairros.filter(
            bairro => bairro.bairro != null && bairro.bairro != ""
        );
        this.neighborhoodDropdownSettings = {
            singleSelection: false,
            idField: "bairro",
            textField: "bairro",
            selectAllText: "Selecionar Todos",
            unSelectAllText: "Limpar Seleção",
            itemsShowLimit: 2,
            allowSearchFilter: true,
            searchPlaceholderText: "Pesquisar",
            noDataAvailablePlaceholderText: "Sem dados disponíveis",
        };
    }

    setUfDropdown() {
        this.ufDropdownList = this.chargeConfig.ufs.filter(uf => uf.estado != null && uf.estado != "");
        this.ufDropdownSettings = {
            singleSelection: true,
            idField: "estado",
            textField: "estado",
            selectAllText: "Selecionar Todos",
            unSelectAllText: "Limpar Seleção",
            itemsShowLimit: 1,
            allowSearchFilter: true,
            searchPlaceholderText: "Pesquisar",
            noDataAvailablePlaceholderText: "Sem dados disponíveis",
        };
    }

    setCollectorDropdown() {
        this.collectorDropdownList = this.collectors.filter(collector => collector.ativo);
        this.collectorDropdownSettings = {
            singleSelection: true,
            idField: "codigo_externo",
            textField: "nome",
            selectAllText: "Selecionar Todos",
            unSelectAllText: "Limpar Seleção",
            itemsShowLimit: 1,
            allowSearchFilter: true,
            searchPlaceholderText: "Pesquisar",
            noDataAvailablePlaceholderText: "Sem dados disponíveis",
        };
    }

    ufChangeEvent(uf: any[]) {
        if (this.chargeConfig !== null && this.chargeConfig !== undefined) {
            this.chargeFilter.city = [];
            if (uf.length > 0) {
                this.cityDropdownList = this.chargeConfig.municipios.filter(
                    municipio => municipio.municipio != null && municipio.municipio != "" && municipio.estado == uf[0].estado
                );
            } else {
                this.cityDropdownList = this.chargeConfig.municipios.filter(
                    municipio => municipio.municipio != null && municipio.municipio != ""
                );
            }
        }
    }

    cityChangeEvent(city: any[]) {
        if (this.chargeConfig !== null && this.chargeConfig !== undefined) {
            this.chargeFilter.neighborhoods = [];
            if (city.length > 0) {
                this.neighborhoodDropdownList = this.chargeConfig.bairros.filter(
                    bairro => bairro.bairro != null && bairro.bairro != "" && bairro.municipio == city[0].municipio
                );
            } else {
                this.neighborhoodDropdownList = this.chargeConfig.bairros.filter(
                    bairro => bairro.bairro != null && bairro.bairro != ""
                );
            }
        }
    }

    async onToggleSelectionCharges(checkAll: boolean) {
        let page: number = 1;
        let limit: number = 50;
        let chargesResult: ChargeResult;
        let totalDocs: number;
        let charges: Charge[] = [];

        if (checkAll) {
            const loader = await this.loaderService.notifica({
                title: "Carregando seleção...",
                date: moment().toISOString(),
                type: "loading",
            });

            chargesResult = await this.chargesDistService
                .getCharges(this.env.selected.cnpj, this.chargeFilter, page, limit)
                .toPromise();

            charges = chargesResult.result;
            totalDocs = chargesResult.totalDocs;

            while (charges.length < totalDocs) {
                page++;
                chargesResult = await this.chargesDistService
                    .getCharges(this.env.selected.cnpj, this.chargeFilter, page, limit)
                    .toPromise();

                charges.push(...chargesResult.result);
            }

            this.chargesLinked = charges.map(charge => {
                return {
                    cgc_cliente: charge.cgc_cliente,
                    contrato: charge.contrato,
                    cobrador: "",
                    date_to: "",
                };
            });

            loader.dismiss();
        } else {
            this.chargesLinked = [];
        }

        this.chargesItemsSelected = this.chargesLinked.length;

        await this.getCharges(this.env.selected.cnpj);
    }

    onChangeSelectionCharge(charge: Charge) {
        if (charge.selected) {
            const linkedIndex = this.chargesLinked.findIndex(
                linked => linked.cgc_cliente === charge.cgc_cliente && linked.contrato === charge.contrato
            );

            if (linkedIndex === -1)
                this.chargesLinked.push({
                    cgc_cliente: charge.cgc_cliente,
                    contrato: charge.contrato,
                    cobrador: "",
                    date_to: "",
                });
        } else {
            this.chargesLinked = this.chargesLinked.filter(
                linked => linked.cgc_cliente !== charge.cgc_cliente && linked.contrato !== charge.contrato
            );
        }

        this.chargesItemsSelected = this.chargesLinked.length;
    }

    async refreshData() {
        this.checkAll = false;
        this.chargesLinked = [];

        // Adiciona o código da área selecionada ao filtro
        if (this.selectedChargeArea) {
            this.chargeFilter.areaCode = this.selectedChargeArea;
        }

        await this.getCharges(this.env.selected.cnpj);
    }

    async onSubmit() {
        const loader = await this.loaderService.notifica({
            title: "Agendando cobranças...",
            date: moment().toISOString(),
            type: "loading",
        });

        this.chargesLinked = this.chargesLinked.map(linked => {
            linked.cobrador = this.collectorSelected;
            linked.date_to = this.schedulingDate.toString();

            return linked;
        });

        const linkedChargesMessage = await this.chargesDistService
            .linkCharges(this.env.selected.cnpj, this.chargesLinked)
            .toPromise();

        if (linkedChargesMessage.message) {
            this.chargesLinked = [];
            this.chargesItemsSelected = this.chargesLinked.length;
            this.addWarnSuccess(linkedChargesMessage.message);
        }

        this.collectorSelected = "";
        this.schedulingDate = null;

        this.getCharges(this.env.selected.cnpj);
        this.modalActive.close();
        loader.dismiss();
    }

    syncChargeByCgc(cgc: string) {
        const chargeSyncPromise = this.chargesDistService.syncChargeByCgc(this.env.selected.cnpj, cgc).toPromise();
        this.subjectSyncCharges$.next(chargeSyncPromise);
    }

    onChangePagination() {
        this.getCharges(this.env.selected.cnpj);
    }

    async openSchedulingModal(modal) {
        if (this.chargesItemsSelected > 0) {
            this.modalActive = this.modalService.open(modal, { size: "lg" });
        } else {
            this.addWarnAlert("Selecione ao menos uma cobrança para prosseguir com agendamento.");
        }
    }

    getCollectorName(cgc: string) {
        const collector = Array.isArray(this.collectors)
            ? this.collectors.find(collector => collector.codigo_externo.includes(cgc))
            : null;
        let collectorName;

        if (collector) {
            collectorName = collector.nome;
        } else {
            collectorName = cgc;
        }

        return collectorName;
    }

    formatErpDate(stringDate: string) {
        if (stringDate !== undefined && stringDate !== "")
            return stringDate.slice(0, 4) + "-" + stringDate.slice(4, 6) + "-" + stringDate.slice(6, 8);
    }

    goBack(): void {
        this.router.navigateByUrl(`/chargesmodule/${this.env.selected.cnpj}`);
    }

    fetchChargeAreas() {
        const cnpj = this.env.selected.cnpj;
        if (cnpj) {
            this.chargesDistService.getChargeAreas(cnpj, 10, 1).subscribe(
                response => {
                    const uniqueAreas = [];
                    const areaSet = new Set<string>();
                    response.result.forEach(area => {
                        const key = `${area.descricao_area}_${area.codigo_area}`;
                        if (!areaSet.has(key)) {
                            areaSet.add(key);
                            uniqueAreas.push(area);
                        }
                    });
                    this.chargeAreas = uniqueAreas;
                },
                error => {
                    console.error("Erro ao buscar áreas de cobrança:", error);
                }
            );
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
